import { CSSResultGroup, TemplateResult, html } from 'lit';
import { LightDomStyles, OutlineElement } from '@phase2/outline-core';
import { customElement, query, property } from 'lit/decorators.js';
import Splide from '@splidejs/splide';
import '../outline-button-baseline/outline-button-baseline';
import '../outline-icon-baseline/outline-icon-baseline';
import buttonStyles from '../outline-button-baseline/outline-button-baseline.css.lit';
import componentStyles from './outline-card-slider.css.lit';
import ctaCardStyles from '../outline-cta-card/outline-cta-card.css.lit';
import '../outline-cta-card/outline-cta-card';

/**
 * The OutlineCardSlider component.
 *
 * This component is a carousel slider for displaying multiple OutlineCtaCard components.
 *
 * @slot - The default slot accepts one or more OutlineCtaCard components.
 */
@customElement('outline-card-slider')
export class OutlineCardSlider extends OutlineElement {
  static styles: CSSResultGroup = [
    componentStyles,
    buttonStyles,
    ctaCardStyles,
  ];
  lightDomStyles = new LightDomStyles(
    this,
    componentStyles,
    this.tagName.toLowerCase()
  );

  /**
   * Toggle alert open/close
   */
  @property({ type: Boolean, reflect: true, attribute: 'has-title' })
  hasTitle = false;

  /**
   * The Splide instance.
   */
  splide: Splide;

  /**
   * The element that contains the Splide component.
   */
  @query('.splide')
  splideEl: HTMLElement;

  /**
   * The previous slide button.
   */
  @query('.splide__arrow--prev')
  prevBtn: HTMLElement;

  /**
   * The next slide button.
   */
  @query('.splide__arrow--next')
  nextBtn: HTMLElement;

  /**
   * The mutation observer that watches the light DOM for changes.
   */
  observer: MutationObserver;

  connectedCallback() {
    super.connectedCallback();

    this.observer = new MutationObserver(() => {
      this.requestUpdate();
    });
    this.observer.observe(this, { childList: true });
  }

  disconnectedCallback() {
    this.observer.disconnect();
  }

  firstUpdated() {
    const slideLength = this.querySelectorAll('[slot="slide"]').length;

    this.splide = new Splide(this.splideEl, {
      perPage: 3,
      breakpoints: {
        1200: {
          perPage: 2,
        },
        768: {
          perPage: 1,
        },
      },
      gap: '1rem',
      keyboard: false,
      arrows: slideLength > 1,
    }).mount();

    if (slideLength <= 1) return;

    // Adds spans to buttons for screen readers.
    this.prevBtn.innerHTML += '<span class="sr-only">Previous Slide</span>';
    this.nextBtn.innerHTML += '<span class="sr-only">Next Slide</span>';
  }

  render(): TemplateResult {
    return html`
      <div class="card-slider ${this.hasTitle ? 'has-title' : ''}">
        <div class="splide">
          <div class="splide__track">
            <div class="splide__list">
              ${(
                Array.from(
                  this.querySelectorAll(`[slot='slide']`)
                ) as HTMLElement[]
              ).map((slideElementLightDOM, index, LightDOMElements) => {
                return html`
                  <div
                    class="splide__slide"
                    role="group"
                    aria-label="slide ${index +
                    1} of ${LightDOMElements.length}"
                  >
                    ${slideElementLightDOM.cloneNode(true)}
                  </div>
                `;
              })}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
